import { Back, Sine, TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { AppProgress } from "../Data/AppProgress";
import * as Constants from "../Data/Constants";
import { GalleryHieroglyphData } from "../Data/GalleryHieroglyphData";
import { GameType } from "../Data/GameData";
import * as SoundEffect from "../Data/SoundEffect";
import * as Topics from "../Data/Topics";
import * as VoiceOver from "../Data/VoiceOver";
import { Button } from "../GameObjects/Button";
import { HieroglyphButton } from "../GameObjects/HieroglyphButton";
import { HieroglyphCanvas } from "../GameObjects/HieroglyphCanvas";
import { HieroglyphColourButton } from "../GameObjects/HieroglyphColourButton";
import { Game } from "../States/Game";

export class GameHieroglyph extends Game {
    private static readonly BUTTON_SPACING: number = 140;

    protected readonly _gameType = GameType.HIEROGLYPH;

    private _buttons: Button[];
    private _canvas: HieroglyphCanvas;

    constructor() {
        super();
        this._funFactCommon = VoiceOver.FUN_FACT_HIERO_COMMON;
        this._funFactUnique = VoiceOver.FUN_FACT_HIERO_UNIQUE;

        this._canvas = new HieroglyphCanvas();
        this._canvas.position.set(0, -80);
        const data: GalleryHieroglyphData = AppProgress.games[this._gameType].saveData[0] as GalleryHieroglyphData;
        if (data !== undefined) {
            for (let i = 0; i < data.contentIndeces.length; ++i) {
                this._canvas.updateLayer(i, data.contentIndeces[i], true);
            }
            this._canvas.updateColour(data.colourIndex);
            this._canvas.updateWeather(data.weatherIndex, true);
        }
        this.addChild(this._canvas);

        this._buttons = new Array<HieroglyphButton>(5);
        for (let i = 0; i < this._buttons.length - 1; ++i) {
            const index: number = i;
            this._buttons[i] = new HieroglyphButton(
                () => { this.onHieroglyphButtonPressed(index); },
                index,
            );
        }
        this._buttons[4] = new HieroglyphColourButton(
            () => { this.onHieroglyphButtonPressed(4); },
            4,
        );
        for (let i = 0; i < this._buttons.length; ++i) {
            this._buttons[i].position.set((i - 2) * GameHieroglyph.BUTTON_SPACING, 300);
            this.addChild(this._buttons[i]);
        }
        this._buttonClear.visible = false;

        this.setChildIndex(this._andy, this.children.length - 1);
        HLF.PixiUtils.sendToFront(this._buttonSubmit);
        HLF.PixiUtils.sendToFront(this._onboardingManager);
        HLF.PixiUtils.sendToFront(this._keyboardFocusManager);
    }

    public static getRequiredAssets(): string[] {
        const required = super.getRequiredAssets();
        for (let i = 0; i < 4; ++i) {
            required.push(Constants.SPRITESHEET_HIERO_PREFIX + "0" + i + HLF.ResourceUtils.FILE_EXTENSION_JSON);
        }
        for (let i = 0; i < 10; ++i) {
            required.push(
                "colour-ramp-" +
                HLF.NumberUtils.addZeroPadding(i.toString(), 2) +
                HLF.ResourceUtils.FILE_EXTENSION_PNG);
        }
        required.push("colour-ramp-night.png");
        return required;
    }

    public init(pSize: PIXI.Point): void {
        super.init(pSize);
        this._canvas.setupForOnboarding(this._isOnboarding);
        for (let i = 0; i < this._buttons.length - 1; ++i) {
            (this._buttons[i] as HieroglyphButton).setupForOnboarding(this._isOnboarding);
        }
        (this._buttons[4] as HieroglyphColourButton).setupForOnboarding(this._isOnboarding);
    }

    public update(pDeltaTime: number): void {
        super.update(pDeltaTime);
        if (this._canvas !== undefined) {
            this._canvas.update(pDeltaTime);
        }
    }

    public animateIn(pOnComplete: () => void): void {
        super.animateIn(pOnComplete);

        this._buttonSubmit.interactive = false;
        for (let i = 0; i < this._buttons.length; ++i) {
            this._buttons[i].interactive = false;
        }

        this._canvas.position.x = -this._size.x * 0.5 - this._canvas.width;
        this._buttonSubmit.position.x = this._size.x * 0.5 + this._buttonSubmit.width;
        for (let i = 0; i < this._buttons.length; ++i) {
            this._buttons[i].position.y = this._size.y + 200;
        }

        const timeline: TimelineLite = new TimelineLite();
        timeline.to(this._canvas.position, 1.0, {
            x: 0,
            ease: Sine.easeOut,
        }, 0);
        if (this._isOnboarding) {
            this.performOnboardingStep(0);
        }
        else {
            for (let i = 0; i < this._buttons.length; ++i) {
                timeline.to(this._buttons[i].position, 1.0, {
                    y: 300,
                    ease: Sine.easeOut,
                }, i * 0.1);
            }
            timeline.to(this._buttonSubmit.position, 1.0, {
                x: this.getSubmitButtonPos().x,
                ease: Sine.easeOut,
            }, 0);
        }
        timeline.call(this.onAnimateInComplete.bind(this));
    }

    public animateOut(pOnComplete: () => void): void {
        super.animateOut(pOnComplete);
        for (let i = 0; i < this._buttons.length; ++i) {
            this._buttons[i].interactive = false;
        }
        this._buttonSubmit.interactive = false;

        const timeline: TimelineLite = new TimelineLite();

        for (let i = 0; i < this._buttons.length; ++i) {
            timeline.to(this._buttons[i], 0.5, {
                y: 475,
                ease: Back.easeIn,
            }, i * 0.1);
        }
        timeline.to(this._buttonSubmit.position, 0.5, {
            x: this._size.x * 0.5 + this._buttonSubmit.width,
            ease: Sine.easeIn,
        }, 0);
        timeline.call(() => {
            PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(
                SoundEffect.LIFT_ART,
                1, false, HLF.AudioCategory.SFX,
            ));
        }, undefined, undefined, 0.5);
        timeline.to(this._canvas, 0.8, {
            x: -this._size.x * 0.5 - this._canvas.width,
            ease: Sine.easeIn,
        }, 0.5);
    }

    public onResize(pSize: PIXI.Point): void {
        super.onResize(pSize);
    }

    protected onAnimateInComplete(): void {
        super.onAnimateInComplete();
        if (this._isOnboarding === false) {
            for (let i = 0; i < this._buttons.length; ++i) {
                this._buttons[i].interactive = true;
            }
            this._buttonSubmit.interactive = true;
            PubSub.publishSync(HLF.REGISTER_FOCUSABLES, this._buttons.concat(this._buttonSubmit));
        }
        else {
            this.populateOnboarding();
        }
        PubSub.publishSync(Topics.KEYBOARD_REFOCUS, undefined);
    }

    protected saveData(): void {
        AppProgress.games[this._gameType].saveData[0] = this._canvas.generateSaveData();
        AppProgress.games[this._gameType].recentItemIndex = 0;
    }

    protected getAndyPos(): PIXI.Point {
        return new PIXI.Point(
            Math.max(this._size.x * 0.5 - this._andy.width * 0.5 - 50, 425),
            this._size.y * 0.5 + this._andy.height * 0.3,
        );
    }

    protected getSubmitButtonPos(): PIXI.Point {
        return new PIXI.Point(
            this._size.x * 0.5 - 90,
            0,
        );
    }

    private populateOnboarding(): void {
        this._onboardingManager.clear();
        this._onboardingManager.addStep(
            Topics.HIEROGLYPH_CHANGED,
            0,
            this._buttons[0],
            4.0,
            true,
            undefined,
            undefined,
            () => { this.performOnboardingStep(1); },
        );
        this._onboardingManager.addStep(
            Topics.HIEROGLYPH_CHANGED,
            1,
            this._buttons[1],
            5.0,
            true,
            undefined,
            undefined,
            () => { this.performOnboardingStep(2); },
        );
        this._onboardingManager.start();
    }

    private onHieroglyphButtonPressed(pIndex: number): void {
        switch (pIndex) {
            case 0:
                this._canvas.updateLayer(2, (this._buttons[pIndex] as HieroglyphButton).iconIndex);
                break;
            case 1:
                this._canvas.updateLayer(1, (this._buttons[pIndex] as HieroglyphButton).iconIndex);
                break;
            case 2:
                this._canvas.updateWeather((this._buttons[pIndex] as HieroglyphButton).iconIndex);
                break;
            case 3:
                this._canvas.updateLayer(0, (this._buttons[pIndex] as HieroglyphButton).iconIndex);
                break;
            case 4:
                this._canvas.updateColour((this._buttons[pIndex] as HieroglyphColourButton).colourIndex);
                break;
        }

        PubSub.publishSync(Topics.HIEROGLYPH_CHANGED, pIndex);
    }

    private performOnboardingStep(pStep: number): void {
        let timeline: TimelineLite;
        switch (pStep) {
            case 0:
                this._buttons[0].position.x = 0;
                timeline = new TimelineLite();
                timeline.add((this._buttons[0] as HieroglyphButton).playScrollAnimation());
                timeline.to(this._buttons[0].position, 1.0, {
                    y: 300,
                    ease: Sine.easeOut,
                }, 0);
                timeline.call(() => {
                    this._canvas.updateLayer(2, 0, true);
                    PubSub.publishSync(HLF.REGISTER_FOCUSABLE, this._buttons[0]);
                });
                break;
            case 1:
                PubSub.publishSync(HLF.UNREGISTER_ALL_FOCUSABLES, undefined);
                this._buttons[1].position.x = GameHieroglyph.BUTTON_SPACING * 0.5;
                timeline = new TimelineLite();
                timeline.add((this._buttons[1] as HieroglyphButton).playScrollAnimation(), 0.75);
                timeline.to(this._buttons[1].position, 1.0, {
                    y: 300,
                    ease: Sine.easeOut,
                }, 0.75);
                timeline.to(this._buttons[0].position, 0.5, {
                    x: -GameHieroglyph.BUTTON_SPACING * 0.5,
                    ease: Sine.easeInOut,
                }, 0.75);
                timeline.call(() => {
                    this._canvas.updateLayer(1, 0, true);
                    PubSub.publishSync(HLF.REGISTER_FOCUSABLES, [ this._buttons[1], this._buttons[0] ]);
                });
                break;
            case 2:
                PubSub.publishSync(HLF.UNREGISTER_ALL_FOCUSABLES, undefined);
                timeline = new TimelineLite();
                timeline.to(this._buttons[0].position, 0.5, {
                    x: -GameHieroglyph.BUTTON_SPACING * 2,
                    ease: Sine.easeInOut,
                }, 0.75);
                timeline.to(this._buttons[1].position, 0.5, {
                    x: -GameHieroglyph.BUTTON_SPACING * 1,
                    ease: Sine.easeInOut,
                }, 0.75);
                for (let i = 2; i < this._buttons.length; ++i) {
                    timeline.to(this._buttons[i].position, 1.0, {
                        y: 300,
                        ease: Sine.easeOut,
                    }, 0.75 + (i - 2) * 0.1);
                }
                timeline.to(this._buttonSubmit, 0.5, {
                    x: Math.max(this._size.x * 0.5 - 120, 425),
                    ease: Sine.easeOut,
                }, 0.95);
                timeline.call(() => {
                    this._canvas.updateLayer(0, 0, true);
                }, undefined, this, 1.0);
                timeline.call(() => {
                    for (let i = 2; i < this._buttons.length; ++i) {
                        this._buttons[i].interactive = true;
                    }
                    this._buttonSubmit.interactive = true;
                    this._isOnboarding = false;
                    PubSub.publishSync(HLF.REGISTER_FOCUSABLES, this._buttons.concat(this._buttonSubmit));
                    PubSub.publishSync(HLF.KEYBOARD_FORCE_NEIGHBOURS, new HLF.Input.KeyboardMapToken(
                        this._buttonSubmit,
                        undefined,
                        this._buttons[4],
                        this._buttons[4],
                        undefined,
                    ));
                });
                break;
        }
    }
}
